import type { Column } from 'react-table';
import type { PagefileMetaFn } from 'vite-plugin-pagefiles';
import { Button, Pane, PaneContent } from '@meterup/atto';
import {
  checkDefinedOrThrow,
  isDefined,
  NetworkInterfaceBadge,
  ServicePlanProductBadge,
  ServicePlanStatusBadge,
} from '@meterup/common';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { Link } from 'react-router-dom';

import type { PlanAndProvider } from '../../../api/controllers_api';
import { fetchServicePlanAndProvider } from '../../../api/controllers_api';
import { AutoTable } from '../../../components/AutoTable/AutoTable';
import { Nav } from '../../../components/Nav';
import { paths } from '../../../constants';
import { useCloseDrawerCallback } from '../../../hooks/useCloseDrawerCallback';
import { makeDrawerLink } from '../../../utils/makeLink';

export const Meta: PagefileMetaFn = () => ({
  path: '/controllers/:controllerName/plans',
});

const columns: Column<PlanAndProvider>[] = [
  {
    Header: 'Status',
    accessor: (p) => p.plan.status,
    Cell: ServicePlanStatusBadge,
  },
  {
    Header: 'Provider',
    accessor: (p) => p.provider?.name,
  },
  {
    Header: 'Product',
    accessor: (p) => p.plan.product,
    Cell: ServicePlanProductBadge,
  },
  {
    Header: 'Interface',
    accessor: (p) => p.plan.network_interface,
    Cell: NetworkInterfaceBadge,
  },
];

export default function ControllerServicePlans() {
  const { controllerName } = checkDefinedOrThrow(
    Nav.useRegionParams('root', paths.pages.ControllerServicePlans),
  );

  const plans =
    useQuery(
      ['controllers', controllerName, 'service-plans'],
      () => fetchServicePlanAndProvider(controllerName),
      { suspense: true },
    ).data ?? [];

  const drawerParams = Nav.useRegionParams('drawer', paths.drawers.ISPDetails);

  const closeDrawer = useCloseDrawerCallback();

  return (
    <PaneContent>
      <Pane>
        <AutoTable
          columns={columns}
          data={plans}
          additionalControls={
            plans.length < 2 && (
              <Button
                as={Link}
                to={makeDrawerLink(paths.drawers.ISPCreate, {
                  controllerName,
                })}
                variant="secondary"
                icon="plus-circle"
                arrangement="leading-icon"
              >
                Create
              </Button>
            )
          }
          onRowDeselect={closeDrawer}
          isRowSelected={(row) => isDefined(drawerParams) && row.plan.sid === drawerParams.planId}
          linkProps={(row) => ({
            to: makeDrawerLink(paths.drawers.ISPDetails, {
              controllerName,
              planId: row.plan.sid,
            }),
          })}
        />
      </Pane>
    </PaneContent>
  );
}
